/**
 * Marketing-site constants — single source for NAP, URLs, and nav paths.
 *
 * PATHS are the clean apex paths. On meridiananesthesia.com, middleware
 * rewrites them to the internal /website/* routes (the browser URL stays
 * clean). Anywhere else — a Vercel preview, a local build — the same pages
 * answer at /website/*, and <SiteLink> adds that prefix so the links still
 * work (components/SiteBase.tsx).
 *
 * CANONICALS always point at the final clean apex URLs, and the robots.txt
 * served to every other host disallows indexing.
 */

export const SITE = {
  name: 'Meridian Anesthesia',
  legalName: 'Meridian Anesthesia, a Division of National Partners in Healthcare',
  apexUrl: 'https://meridiananesthesia.com',
  platformUrl: 'https://platform.meridiananesthesia.com',
  loginUrl: 'https://platform.meridiananesthesia.com/login',
  // The practice line: a Google Voice number that rings the practice manager.
  // The candidate portal and every public page read it from here, so there is
  // one public number. Call a number to confirm it rings before entering it.
  phoneDisplay: '(903) 213-5402',
  phoneTel: '+19032135402',
  email: 'admin@meridiananesthesia.com',
  address: {
    street: '2221 Lakeside Blvd, Suite 600',
    city: 'Richardson',
    state: 'TX',
    zip: '75082',
  },
  description:
    'Meridian Anesthesia, a Division of National Partners in Healthcare, is an anesthesia group of physicians and CRNAs at multiple hospitals in Dallas–Fort Worth.',
  // The parent organization, for the site's structured data only — the pages
  // do not link out to it (content.ts header).
  parent: { name: 'National Partners in Healthcare', url: 'https://nphllc.com' },
} as const

export const PATHS = {
  home: '/',
  about: '/about',
  services: '/services',
  team: '/team',
  careers: '/careers',
  apply: '/apply',
  contact: '/contact',
} as const

/** Final clean apex URLs (canonical tags, sitemap, JSON-LD). */
export const CANONICALS = {
  home: `${SITE.apexUrl}/`,
  about: `${SITE.apexUrl}/about`,
  services: `${SITE.apexUrl}/services`,
  team: `${SITE.apexUrl}/team`,
  careers: `${SITE.apexUrl}/careers`,
  apply: `${SITE.apexUrl}/apply`,
  contact: `${SITE.apexUrl}/contact`,
} as const

export const OG_IMAGE = {
  url: '/og/meridian-og-v2.png',
  width: 1200,
  height: 630,
  alt: 'Meridian Anesthesia, a Division of National Partners in Healthcare — DFW’s Premier Anesthesia Practice',
} as const

/**
 * Full OpenGraph object for a page. Next.js metadata merging REPLACES the
 * layout's `openGraph` wholesale when a page sets one, so each page carries
 * the complete object (subpages must not inherit the homepage og:url).
 */
export function pageOpenGraph(canonical: string, title: string, description: string) {
  return {
    type: 'website' as const,
    siteName: SITE.name,
    locale: 'en_US',
    url: canonical,
    title: `${title} | ${SITE.name}`,
    description,
    images: [OG_IMAGE],
  }
}

export const NAV_LINKS: { label: string; href: string }[] = [
  { label: 'About', href: PATHS.about },
  { label: 'Services', href: PATHS.services },
  { label: 'Our Team', href: PATHS.team },
  { label: 'Careers', href: PATHS.careers },
  { label: 'Contact', href: PATHS.contact },
]
